/**
 * ScenarioDialog (Feature A3 — Scenario Sequencing)
 * =================================================
 * Runs an A→B comparison on the same cohort of a completed simulation.
 * The user enters an intervention ("+$5k signing bonus", "20% price cut"),
 * we re-interview every persona with the modified stimulus, then render
 * before/after distribution bars + shift counts + biggest-shift callout
 * and a persona-level delta table.
 */
import { useEffect, useState, useMemo } from 'react';
import api from '../lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  GitCompareArrows,
  Loader2,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

const DIRECTION_META = {
  up:      { color: 'text-emerald-600',   bg: 'bg-emerald-50',  Icon: ArrowUp,    label: 'Up'      },
  down:    { color: 'text-red-600',      bg: 'bg-red-50',     Icon: ArrowDown,  label: 'Down'    },
  same:    { color: 'text-slate-500',     bg: 'bg-slate-50',    Icon: ArrowRight, label: 'Same'    },
  changed: { color: 'text-blue-600',    bg: 'bg-blue-50',   Icon: ArrowRight, label: 'Changed' },
};

const pctOf = (n, total) => (total ? (n / total) * 100 : 0);

const attrPreview = (attrs) => {
  if (!attrs) return '';
  return Object.entries(attrs).filter(([k]) => k !== 'id').slice(0, 3).map(([k, v]) => `${k}: ${v}`).join(' · ');
};

const BarRow = ({ label, beforeCount, afterCount, total, palette }) => {
  const b = pctOf(beforeCount, total);
  const a = pctOf(afterCount, total);
  return (
    <div className="grid grid-cols-[110px_1fr_1fr] items-center gap-3 text-xs">
      <div className="font-medium text-slate-700 truncate" title={label}>{label}</div>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full" style={{ width: `${b}%`, background: palette }} />
        </div>
        <span className="text-slate-500 w-16 text-right tabular-nums">{beforeCount} · {b.toFixed(0)}%</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full" style={{ width: `${a}%`, background: palette }} />
        </div>
        <span className="text-slate-500 w-16 text-right tabular-nums">{afterCount} · {a.toFixed(0)}%</span>
      </div>
    </div>
  );
};

const BAR_COLORS = [
  'linear-gradient(to right, #10b981, #34d399)',
  'linear-gradient(to right, #22c55e, #4ade80)',
  'linear-gradient(to right, #94a3b8, #cbd5e1)',
  'linear-gradient(to right, #f97316, #fb923c)',
  'linear-gradient(to right, #ef4444, #f87171)',
];

export default function ScenarioDialog({ open, onOpenChange, simulation }) {
  const [modifier, setModifier] = useState('');
  const [name, setName] = useState('');
  const [sampleSize, setSampleSize] = useState('');
  const [running, setRunning] = useState(false);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);

  const simulationId = simulation?.id;

  useEffect(() => {
    if (!open) return;
    setModifier(''); setName(''); setSampleSize(''); setCurrent(null);
  }, [open, simulationId]);

  useEffect(() => {
    if (!open || !simulationId) return;
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/simulations/${simulationId}/scenarios`);
        if (alive) setHistory(r.data || []);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [open, simulationId]);

  const reloadHistory = async () => {
    try {
      const r = await api.get(`/simulations/${simulationId}/scenarios`);
      setHistory(r.data || []);
    } catch { /* ignore */ }
  };

  const handleRun = async () => {
    if (!modifier.trim() || modifier.trim().length < 3) {
      toast.error('Please describe the intervention (at least 3 characters)');
      return;
    }
    setRunning(true);
    setCurrent(null);
    try {
      const payload = { modifier: modifier.trim() };
      if (name.trim()) payload.name = name.trim();
      if (sampleSize && !Number.isNaN(parseInt(sampleSize, 10))) payload.sample_size = parseInt(sampleSize, 10);
      const res = await api.post(`/simulations/${simulationId}/scenarios`, payload);
      setCurrent(res.data);
      toast.success(`Scenario complete — ${res.data.total_personas} personas re-answered`);
      reloadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scenario run failed');
    } finally {
      setRunning(false);
    }
  };

  const handleLoad = async (scenarioId) => {
    try {
      const res = await api.get(`/scenarios/${scenarioId}`);
      setCurrent(res.data);
    } catch (err) {
      toast.error('Could not load scenario');
    }
  };

  const handleDelete = async (scenarioId) => {
    try {
      await api.delete(`/scenarios/${scenarioId}`);
      toast.success('Scenario deleted');
      if (current?.id === scenarioId) setCurrent(null);
      reloadHistory();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const scale = current?.response_scale || [];
  const total = current?.total_personas || 0;
  const ds = current?.delta_summary || {};

  const topShifts = useMemo(() => {
    if (!current) return [];
    const counts = {};
    for (const r of (current.responses || [])) {
      if (r.before_response === r.after_response) continue;
      const key = `${r.before_response} → ${r.after_response}`;
      counts[key] = (counts[key] || 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 3);
  }, [current]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="scenario-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCompareArrows className="w-5 h-5 text-blue-600" />
            Scenario Sequencing
          </DialogTitle>
          <DialogDescription>
            Inject an intervention into <span className="font-medium text-slate-700">{simulation?.name}</span> and measure how the same cohort re-answers. Great for board decks ("if we offered +$5k bonus, 23% shift from disagree → agree").
          </DialogDescription>
        </DialogHeader>

        {/* Inputs */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2 space-y-2">
            <Label>Intervention / modifier</Label>
            <Textarea
              value={modifier}
              onChange={e => setModifier(e.target.value)}
              placeholder="e.g. Imagine the price is reduced by 20% and 24/7 support is included"
              rows={2}
              maxLength={1000}
              data-testid="scenario-modifier"
            />
          </div>
          <div className="space-y-2">
            <Label>Name (optional)</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Price drop + support" data-testid="scenario-name" />
            <Label className="pt-1">Sample size (optional)</Label>
            <Input
              type="number"
              min={1}
              max={500}
              value={sampleSize}
              onChange={e => setSampleSize(e.target.value)}
              placeholder={`All (${simulation?.sample_size || 'n/a'})`}
              data-testid="scenario-sample-size"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="scenario-close">Close</Button>
          <Button onClick={handleRun} disabled={running} data-testid="scenario-run">
            {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Running…</> : <>Run scenario</>}
          </Button>
        </div>

        {/* Results */}
        {current && (
          <div className="space-y-4 border-t pt-4" data-testid="scenario-results">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">{current.name}</h3>
                <p className="text-xs text-slate-500 italic mt-0.5">"{current.modifier}"</p>
              </div>
              <Badge variant="outline" className="text-[10px]">{current.total_personas} personas</Badge>
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                ['up', ds.shifted_up],
                ['down', ds.shifted_down],
                ['same', ds.unchanged],
              ].map(([dir, count]) => {
                const meta = DIRECTION_META[dir];
                return (
                  <div key={dir} className={`${meta.bg} border border-transparent rounded-lg px-3 py-2 flex items-center gap-2`}>
                    <meta.Icon className={`w-4 h-4 ${meta.color}`} />
                    <div>
                      <div className={`text-lg font-bold ${meta.color} tabular-nums`}>{count || 0}</div>
                      <div className="text-[10px] uppercase text-slate-500 tracking-wide">{meta.label}</div>
                    </div>
                  </div>
                );
              })}
              {ds.biggest_shift && (
                <div className="bg-blue-50 rounded-lg px-3 py-2">
                  <div className="text-[10px] uppercase text-slate-500 tracking-wide mb-0.5">Biggest shift</div>
                  <div className="text-xs font-medium text-slate-800">
                    {ds.biggest_shift.from} → {ds.biggest_shift.to}
                  </div>
                  <div className="text-[10px] text-slate-500">× {ds.biggest_shift.count}</div>
                </div>
              )}
            </div>

            {/* Before / After distribution */}
            <div className="space-y-2">
              <div className="grid grid-cols-[110px_1fr_1fr] gap-3 text-[11px] uppercase tracking-wide text-slate-500">
                <div />
                <div>Before</div>
                <div>After</div>
              </div>
              {scale.map((label, i) => (
                <BarRow
                  key={label}
                  label={label}
                  beforeCount={current.distribution_before?.[label] || 0}
                  afterCount={current.distribution_after?.[label] || 0}
                  total={total}
                  palette={BAR_COLORS[i % BAR_COLORS.length]}
                />
              ))}
            </div>

            {/* Top shifts callout */}
            {topShifts.length > 0 && (
              <div className="bg-slate-50 rounded-md px-3 py-2">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Top transitions</div>
                <div className="flex flex-wrap gap-1.5">
                  {topShifts.map(([label, count]) => (
                    <Badge key={label} variant="secondary" className="text-[11px]">{label} · {count}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Per-persona delta table */}
            <div className="text-xs">
              <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Persona-level delta</div>
              <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-md">
                <table className="w-full">
                  <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-2 py-1.5">Persona</th>
                      <th className="text-left px-2 py-1.5">Before</th>
                      <th className="text-left px-2 py-1.5">After</th>
                      <th className="text-left px-2 py-1.5">Direction</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(current.responses || []).map((r, i) => {
                      const meta = DIRECTION_META[r.direction] || DIRECTION_META.same;
                      return (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="px-2 py-1.5 text-slate-600 truncate max-w-[280px]" title={attrPreview(r.attributes)}>{attrPreview(r.attributes)}</td>
                          <td className="px-2 py-1.5">{r.before_response}</td>
                          <td className="px-2 py-1.5 font-medium text-slate-800">{r.after_response}</td>
                          <td className="px-2 py-1.5">
                            <span className={`inline-flex items-center gap-1 ${meta.color}`}>
                              <meta.Icon className="w-3 h-3" /> {meta.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* History */}
        {history.length > 0 && !current && (
          <div className="space-y-2 border-t pt-4" data-testid="scenario-history">
            <h3 className="font-semibold text-slate-900 text-sm">Past scenarios</h3>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {history.map(h => (
                <div key={h.id} className="flex items-center justify-between gap-2 text-xs bg-slate-50 hover:bg-slate-100 rounded-md px-3 py-2 cursor-pointer" onClick={() => handleLoad(h.id)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className="text-[10px]">{h.total_personas} personas</Badge>
                      <span className="text-slate-500 text-[10px]">{(h.delta_summary?.shifted_up ?? 0)}↑ / {(h.delta_summary?.shifted_down ?? 0)}↓ / {(h.delta_summary?.unchanged ?? 0)}=</span>
                    </div>
                    <div className="text-slate-700 truncate mt-0.5">{h.name}</div>
                    <div className="text-[10px] text-slate-400 italic truncate">"{h.modifier}"</div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                    onClick={(e) => { e.stopPropagation(); handleDelete(h.id); }}
                    data-testid={`scenario-history-delete-${h.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
